/**
 * 定义vue全局过滤器方法，会在main.js里循环定义vue全局过滤器
 */
import {formatDate} from '@/util/date-format'
import i18n from '@/lang'

const monthList: string[] = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const day: any = ['Mon', 'Tue', 'Wed', 'Thur', 'Fri', 'Sat', 'Sun']

const filters = {

    /**
     * 格式化日期
     * @param time             要转换的值
     * @param empty  {String}  time为假值时的返回值
     * @param format {String}  日期格式
     * @returns      {string}  格式化后的字符串 | empty
     */
    dateFilter(time: any, empty = '', format = 'dd/MM/yyyy'): string {
        let originTime = time
        if (time) {
            if (typeof time === 'string') {
                time = time.replace(/\-/g, '/')
                const value = new Date(time).getTime()
                if (Number.isNaN(value)) {
                    time = originTime
                }
            }
            return formatDate(new Date(time), format)
        }
        return empty
    },

    /**
     * 格式化日期 加月份
     * @param time             要转换的值
     * @param empty  {String}  time为假值时的返回值
     * @param format {String}  日期格式
     * @returns      {string}  格式化后的字符串 | empty
     */
    dateDayFilter(time: any, empty = '', format = 'dd/MM/yyyy'): string {
        if (time) {
            return formatDate(new Date(time), format) + ', ' + day[(new Date(time).getDay() === 0) ? 6 : (new Date(time).getDay() - 1)]
        }
        return empty
    },

    /**
     * 示例 18 AUG 2022
     */
    dateMonthFilter(time: any, empty = ''): string {
        if (time) {
            const date = new Date(time)
            const _day = date.getDate()
            const month = monthList[date.getMonth()]
            const year = date.getFullYear()
            return _day + ' ' + month + ' ' + year
        }
        return empty
    },

    /**
     * 格式化日期时间
     * @param time            要转换的值
     * @param empty  {String} time为假值时的返回值
     * @param format {String} 日期格式
     * @returns      {string} 格式化后的字符串 | empty
     */

    dateTimeFilter(time: any, empty = '', format = 'dd/MM/yyyy hh:mm:ss'): string {
        if (time) {
            return formatDate(new Date(time), format)
        }
        return empty
    },

    /**
     * 单独或组合获取日期中的字段
     * @param time             要转换的值
     * @param empty  {String}  time为假值时的返回值
     * @param format {String}  日期格式
     * @returns      {string}  格式化后的字符串 | empty
     */
    dateFieldFilter(time: any, empty = '', format = 'yyyy'): string {
        if (time) {
            if (format === 'M') {
                return monthList[+formatDate(new Date(time), format) - 1]
            }
            return formatDate(new Date(time), format)
        }
        return empty
    },

    /**
     * 格式化数字，保留number位小数
     * @param number          要格式化的数字
     * @param empty  {String} number不能被转换为Number时的返回值
     * @param digits {Number} 小数位数
     * @returns      {Number|String} 格式化后的数字 | empty
     */
    fixedFilter(number: any, empty = '', digits = 2): string {
        if (number == null) {   // null强转Number的值为0，先处理
            return empty
        }
        number = +number
        return Number.isNaN(number) ? empty : NP.toFixed(number, digits)
    },

    /**
     * 格式化%数字，保留number位小数
     * @param number          要格式化的数字
     * @param empty {String} number不能被转换为Number时的返回值
     * @param digits {Number} 小数位数
     * @returns      {Number|String} 格式化后的数字加% | empty
     */
    percentageFilter(number: any, empty = '', digits = 2) {
        if (number == null) {   // null强转Number的值为0，先处理
            return empty
        }
        number = +number * 100
        return Number.isNaN(number) ? empty : (NP.toFixed(number, digits) + '%')
    },

    /**
     * 对undefined,null,NaN等假值进行处理
     * @param value          要处理的值
     * @param empty {String} value为假值时的返回值
     * @returns     {string} value | empty
     */
    emptyFilter(value: any, empty = ''): string {
        return (value == null || Number.isNaN(value)) ? empty : value
    },

    /**
     * 统计数组中每个对象的某个字段之和
     * @param arr    {Array}         要统计的数组
     * @param key    {String}        要统计的字段
     * @param empty  {Number|String} 统计结果为假值时的返回值
     * @param digits {Number}        小数位数
     * @returns      {Number|String} 统计结果 | empty
     */
    countFilter(arr: any[], key: string, empty = 0, digits = 2): string | number {
        let sum: number = 0
        if (Array.isArray(arr)) {
            sum = arr.reduce((num, item) => {
                let v: number = +item[key]
                return Number.isNaN(v) ? num : NP.plus(num, v)
            }, 0)
        }
        return sum ? NP.toFixed(sum, digits) : empty
    },

    /**
     * 统计数组中每一个对象的两个字段的乘积之和
     * @param arr    {Array}         要统计的数组
     * @param key1   {String}        要统计的字段
     * @param key2   {String}        要统计的字段
     * @param empty  {Number|String} 统计结果为假值时的返回值
     * @param digits {Number}        小数位数
     * @returns      {Number|String} 统计结果 | empty
     */
    amountFilter(arr: any[], key1: string, key2: string, empty = 0, digits = 2): string | number {
        let sum: number = 0
        if (Array.isArray(arr)) {
            sum = arr.reduce((num, item) => {
                let v1: number = +item[key1]
                let v2: number = +item[key2]
                return Number.isNaN(v1) || Number.isNaN(v2) ? num : NP.plus(num, NP.times(v1, v2))
            }, 0)
        }
        return sum ? NP.toFixed(sum, digits) : empty
    },

    /**
     * 根据一个值从数组中找到匹配对象，并返回匹配对象中的另一个字段
     * @param id                        要查找的值
     * @param arr       {Array}         查找的数组
     * @param idKey     {String}        要查找的值对应的key
     * @param targetKey {String}        要返回的值的key
     * @param empty     {Number|String} 未找到匹配对象时的返回值
     * @returns         {Number|String} 匹配对象中idKey的值 | empty
     */
    getValueById(id: string | number, arr: any[], idKey = 'value', targetKey = 'name', empty = ''): string | number {
        let item: any
        if (Array.isArray(arr)) {
            item = arr.find(v => v[idKey] === id)
        }
        return item ? item[targetKey] : empty
    },

    /**
     * 根据一个值从数组中找到匹配对象，并返回匹配对象中的另一个字段，没找到返回id
     * @param id                        要查找的值
     * @param arr       {Array}         查找的数组
     * @param additional     {String}   未找到匹配对象时的附加内容
     * @param idKey     {String}        要查找的值对应的key
     * @param targetKey {String}        要返回的值的key
     * @returns         {Number|String} 匹配对象中idKey的值 | empty
     */
    getValueById2(id: string | number, arr: any[], additional = '', idKey = 'value', targetKey = 'name'): string | number {
        let item: any
        if (Array.isArray(arr)) {
            item = arr.find(v => v[idKey] === id)
        }
        return item
            ? item[targetKey]
            : (id != null ? (id + additional) : '')
    },
    getValueById3(id: string | number, arr: any[], idKey = 'value', targetKey = 'name', empty = ''): string | number {
        let item: any
        if (Array.isArray(arr)) {
            item = arr.find(v => v[idKey] === id)
        }
        return item ? item[targetKey].slice(0, 2) : empty
    },
    /**
     * 格式化多选下拉框的显示内容
     * @param value     {Array}         多选选中的数组
     * @param list      {Array}         多选下拉框list数据
     * @param valueKey  {String}        value的键
     * @param nameKey   {String}        name的键
     * @param empty     {String}        空值
     * @returns         {Number|String} 格式化后的数字 | empty
     */
    multiSelectString(value:any[], list:any[], valueKey = 'value', nameKey = 'name', empty = ''): string {
        if (!Array.isArray(value) || !Array.isArray(list)) {
            return empty
        }
        const result: any[] = []
        value.forEach(v => {
            const target = list.find(item => item[valueKey] === v)
            if (target) {
                result.push(target[nameKey])
            }
        })
        return result.join(', ')
        // return value.reduce((s, v) => s + ', ' + (list.find(item => item[valueKey] === v) || {})[nameKey], '').slice(1)
    },

    /**
     * 将数字格式化为千分制
     * @param str                要格式化的数字
     * @param empty    {String}  str为假值时的返回值
     * @param diffZero {Boolean} str为假值时是否区分0,为false时返回empty，为true时返回0.00(digits位小数)
     * @param digits   {Number}  小数位数
     * @returns        {String}  格式化后的字符串 | empty
     */
    thousandBitSeparator(str: any, empty = '', diffZero = true, digits = 2): string {
        if (!str && !(diffZero && (str === 0 || str === '0'))) {
            return empty
        }
        let num: number = parseFloat(str)
        if (Number.isNaN(num)) {
            return empty
        }
        return NP.toFixed(num, digits).replace(/(\d)(?=(\d{3})+(\.|\b))/g, ($0, $1) => $1 + ',')
    },

    toUpperCase(str: string): string {
        if (str) {
            return str.toUpperCase()
        }
        return ''
    },
    petFilter(list) {
        if (!list) {
            list = []
        }
        let result = ''
        if (list && list.length) {
            result = list.length + ` ${i18n.t('pets')}: `
            if (list.some((item, index) => {
                if (index > 0 && item.type !== list[index - 1].type) {
                    return true
                }
            })) {
                let keySet = new Set()
                let count = {
                }
                list.forEach(item => {
                    keySet.add(item.type)
                    if (count[item.type]) {
                        count[item.type]++
                    } else {
                        count[item.type] = 1
                    }
                })
                keySet.forEach((type: any) => {
                    result += count[type] + ' ' + type + ', '
                })
                result = result.slice(0, -2)
            } else {
                let item = list[0]
                result = list.length + ' ' + item.type
            }
            return result
        }
    },

    childInfoFilter(childAges: any = [], infantAges: any = []) {
        let result = ''
        let list = childAges.concat(infantAges)
        // list = list.filter(item => (item.haveChild || item.haveInfant))
        list = list.filter(item => (item.age || (item.unit === 'newborn' && !item.age)))
        if (list && list.length) {
            result = result + list.length + ' Children('
            if (list.some((item, index) => {    // 判断是否有不同的age、unit
                if (index > 0 && (item.age !== list[index - 1].age || item.unit !== list[index - 1].unit)) {
                    return true
                }
            })) {
                let count = {
                    year: [],
                    month: [],
                    newborn: [],
                    newbornOtw: [],
                }
                list.forEach(item => {
                    count[item.unit].push(item.age)
                })
                if (count.newbornOtw.length) {
                    result += count.newbornOtw.length + ' Newborn otw, '
                }
                if (count.newborn.length) {
                    let newbornResult = ''
                    for (let i = 0; i < count.newborn.length; i++) {
                        newbornResult += ' Newborn, '
                    }
                    result += newbornResult
                }
                if (count.month.length) {
                    result += count.month.sort((a, b) => a - b).join(',') + (count.month.some(v => v > 1) ? ' months' : ' month') + ', '
                }
                if (count.year.length) {
                    result += count.year.sort((a, b) => a - b).join(',') + (count.year.some(v => v > 1) ? ' yo' : ' yo') + ' , '
                }
                result = result.slice(0, -2)
            } else if (list.length > 0) {    // 所有child年龄，单位相同
                let item = list[0]
                switch (item.unit) {
                    case 'year':
                        result += item.age + (item.age > 1 ? ' yo' : ' yo')
                        break
                    case 'month':
                        result += item.age + (item.age > 1 ? ' months' : ' month')
                        break
                    case 'newborn':
                        result += ' Newborn'
                        break
                    case 'newbornOtw':
                        result += ' Newborn otw'
                        break
                    default:
                        break
                }
            }
            result += ')'
        }
        if (result.charAt(result.length - 1) === ' ') {
            result = result.substring(0, result.length - 2)
        }
        return result
    },

    workTimeFilter(workTime, empty = '') {
        if (!workTime) return empty
        workTime = workTime.split('~')
        const start = workTime[0].trim() ? filters.dateFilter(+workTime[0], '', 'MM/yyyy') : ''
        const end = workTime[1].trim() ? filters.dateFilter(+workTime[1], '', 'MM/yyyy') : ''
        return start + ' ~ ' + end
    },

    helperNameFilter(name: string = '') {
        if (!name) return name
        return String(name).split(' ')[0]
    },

    hkFamilyCompositionFilter(data) {
        const result: string[] = []
        if (+data.noOfAdult) {
            result.push(data.noOfAdult + ' Adult')
        }
        if (+data.noOfChildrenLevelTwo) {
            result.push(data.noOfChildrenLevelTwo + ' child(5-18)')
        }
        if (+data.noOfChildrenLevelOne) {
            result.push(data.noOfChildrenLevelOne + ' child(below 5)')
        }
        if (+data.noOfNewBornBabyComing) {
            result.push(data.noOfNewBornBabyComing + ' newborn baby coming in ' + data.newBornBabyComing)
        }
        return result.join(', ')
    },
    actualHelperQueryFieldsFilter(data) {
        const keyMap = {
            age: 'ageMin',
            salary: 'salaryMin',
            height: 'heightMin',
            weight: 'weightMin',
        }
        return data.actualHelperQueryFields.map(k =>  {
            const o = data.requirementSystemParsed.extendInfo[keyMap[k] || k]
            return o ? o.priority : null
        }).filter(item => item).sort((a, b) => b - a).join(', ')
    },
}

export default filters
